import type { JSX, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Layers,
  Loader2,
  MessageSquarePlus,
  ScanSearch,
  Send,
  Trophy,
  UserPlus,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PageHeader } from '@/components/ui/page-header';
import { TeamPipeline } from '@/components/home/TeamPipeline';
import {
  useAdminAccessRequests,
  useAdminChallenges,
  useAdminCreatorRequests,
  useAdminCronRuns,
  useAdminPayroll,
} from '@/hooks/useAdmin';
import { useScreenshotAuditQueue } from '@/hooks/useScreenshotAudit';
import { useTeamScope } from '@/hooks/useTeamScope';
import { useAdminProspectFolders } from '@/hooks/useAdminProspects';
import { outstandingSummary, payrollRowState } from '@/lib/payroll';
import { pendingPrizeCount } from '@/lib/challenges';
import { rowsInScope } from '@/lib/teams';
import { formatMoney } from '@/lib/money';
import { env } from '@/lib/env';
import { cn } from '@/lib/utils';

const OPEN_REQUEST_STATUSES = 'REQUESTED,IN_PROGRESS,AWAITING_RESPONSE,AWAITING_RATE';
const STALE_FOLDER_DAYS = 7;
const DAY = 24 * 60 * 60 * 1000;

/**
 * Admin home: what needs someone today. Every count links to the list behind
 * it, already filtered. Built only from data other pages already load.
 */
export function AdminHome(): JSX.Element {
  // Live: only invites still in play (not expired, rejected or hidden).
  // Counts follow the same team scope as the pages they open (by default
  // Social Media + Vyral),
  // so a tile never promises rows its list won't show.
  const scope = useTeamScope();
  const proofs = useScreenshotAuditQueue({
    state: 'pending',
    limit: 1,
    live: true,
    team: scope.team || undefined,
    excludeTeam: scope.params.excludeTeam,
  });
  const payroll = useAdminPayroll();
  const access = useAdminAccessRequests('PENDING');
  const requests = useAdminCreatorRequests({
    status: OPEN_REQUEST_STATUSES,
    limit: 1,
    targetTeam: scope.team || undefined,
    excludeTeam: scope.params.excludeTeam,
  });
  const cron = useAdminCronRuns();
  const folders = useAdminProspectFolders();
  const challenges = useAdminChallenges();
  const prizesToApprove = pendingPrizeCount(challenges.data);

  const rows = rowsInScope(payroll.data?.inviters ?? [], scope);
  const states = rows.map((r) => payrollRowState(r));
  const blocked = states.filter((s) => s === 'blocked').length;
  const ready = states.filter((s) => s === 'ready').length;
  const inFlight = states.filter((s) => s === 'in_flight').length;
  const { owed: outstanding, credit, creditCount } = outstandingSummary(rows);

  const weekAgo = Date.now() - 7 * DAY;
  const failedRuns = (cron.data?.groups ?? []).flatMap((g) =>
    g.runs.filter((r) => r.status === 'FAILED' && new Date(r.startedAt).getTime() >= weekAgo),
  ).length;
  const staleFolders = (folders.data ?? []).filter(
    (f) => f.available > 0 && (f.oldestUnclaimedDays ?? 0) >= STALE_FOLDER_DAYS,
  ).length;

  // Only what needs someone shows; a tile with nothing in it is noise. Tiles
  // stay while loading so the grid doesn't jump in from empty.
  const inbox = (
    [
      {
        key: 'proofs',
        icon: ScanSearch,
        loading: proofs.isLoading,
        count: proofs.data?.pendingTotal ?? 0,
        label: 'live proofs to review',
        detail: 'Invites still in play whose screenshot nobody has checked.',
        to: '/admin/screenshot-audit',
      },
      {
        key: 'blocked',
        icon: Wallet,
        loading: payroll.isLoading,
        count: blocked,
        label: 'outreachers owed money but blocked',
        detail: 'Missing payment details or no payout route.',
        to: '/admin/payroll?state=blocked',
        tone: 'warn',
      },
      {
        key: 'access',
        icon: UserPlus,
        loading: access.isLoading,
        count: access.data?.meta.total ?? access.data?.data.length ?? 0,
        label: 'access requests',
        detail: 'People asking to become outreachers.',
        to: '/admin/outreachers?view=access',
      },
      {
        key: 'requests',
        icon: MessageSquarePlus,
        loading: requests.isLoading,
        count: requests.data?.meta.total ?? 0,
        label: 'open creator requests',
        detail: "Campaign manager asks that aren't finished.",
        to: '/admin/creator-requests?status=open',
      },
      {
        key: 'cron',
        icon: AlertCircle,
        loading: cron.isLoading,
        count: failedRuns,
        label: 'failed scheduled jobs this week',
        detail: 'Morning checks, payout syncs or expiries that errored.',
        to: '/admin/system?tab=jobs',
        tone: 'warn',
      },
      {
        key: 'prizes',
        icon: Trophy,
        // Only once there's one to approve: no placeholder while loading.
        loading: false,
        count: prizesToApprove,
        label: prizesToApprove === 1 ? 'challenge prize to approve' : 'challenge prizes to approve',
        detail: 'Ended challenges wait for you before any prize is paid.',
        to: '/admin/challenges',
      },
      env.discoveryApiUrl && {
        key: 'folders',
        icon: Layers,
        loading: folders.isLoading,
        count: staleFolders,
        label: `lead folders untouched for ${STALE_FOLDER_DAYS}+ days`,
        detail: 'Open leads nobody has messaged.',
        to: '/admin/prospects?stale=1',
      },
    ] satisfies (InboxItemProps & { key: string } | '' | undefined)[]
  ).filter((i): i is InboxItemProps & { key: string } => !!i && (i.loading || i.count > 0));

  return (
    <div className="space-y-6">
      <PageHeader title="Home" description="What needs someone today. Each count opens the list behind it." />

      <TeamPipeline />

      <section aria-labelledby="inbox-h" className="space-y-3">
        <h2 id="inbox-h" className="text-[0.9375rem] font-bold">
          Needs attention
        </h2>
        {inbox.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-success-strong" strokeWidth={1.85} aria-hidden />
            Nothing needs attention.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {inbox.map(({ key, ...item }) => (
              <InboxItem key={key} {...item} />
            ))}
          </div>
        )}
      </section>

      <section aria-labelledby="money-h" className="space-y-3">
        <h2 id="money-h" className="text-[0.9375rem] font-bold">
          Money
        </h2>
        <div className="grid gap-3 sm:grid-cols-3">
          <MoneyTile
            label="Outstanding (all periods)"
            value={payroll.isLoading ? null : `$${formatMoney(outstanding)}`}
            hint={
              creditCount > 0
                ? `Still owed to active outreachers. Not counted: $${formatMoney(credit)} paid ahead to ${creditCount} ${creditCount === 1 ? 'outreacher' : 'outreachers'}.`
                : 'Still owed to active outreachers, after adjustments.'
            }
            to="/admin/payroll"
          />
          <MoneyTile
            label="Ready to pay"
            value={payroll.isLoading ? null : String(ready)}
            hint="Outreachers owed money with payment details in place."
            to="/admin/payroll?state=ready"
          />
          <MoneyTile
            label="Payouts in progress"
            value={payroll.isLoading ? null : String(inFlight)}
            hint="Queued or sent to the provider, not yet paid."
            to="/admin/payouts?status=SUBMITTED"
            icon={Send}
          />
        </div>
      </section>
    </div>
  );
}

interface InboxItemProps {
  icon: LucideIcon;
  count: number;
  label: string;
  detail: string;
  to: string;
  loading: boolean;
  tone?: 'default' | 'warn';
}

function InboxItem({ icon: Icon, count, label, detail, to, loading, tone = 'default' }: InboxItemProps): JSX.Element {
  const warn = tone === 'warn' && !loading;
  return (
    <Link
      to={to}
      className={cn(
        'panel group flex items-center gap-3.5 rounded-xl p-4 transition-colors hover:bg-accent/60',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
        warn && 'border-destructive/40',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-[0.625rem] border',
          warn
            ? 'border-destructive/30 bg-destructive/10 text-destructive-strong'
            : 'border-brand/25 bg-brand/10 text-brand',
        )}
      >
        <Icon className="h-4 w-4" strokeWidth={1.85} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className={cn('font-mono text-xl font-medium tracking-[-0.03em] tabular-nums', warn && 'text-destructive-strong')}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : count.toLocaleString()}
          </span>
          <span className="text-sm font-medium">{label}</span>
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">{detail}</p>
      </div>
      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
    </Link>
  );
}

function MoneyTile({
  label,
  value,
  hint,
  to,
  icon: Icon = Wallet,
}: {
  label: string;
  value: ReactNode | null;
  hint: string;
  to: string;
  icon?: LucideIcon;
}): JSX.Element {
  return (
    <Link
      to={to}
      className="panel group rounded-xl p-4 transition-colors hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
    >
      <div className="flex items-center gap-1.5 text-[0.8125rem] font-semibold text-muted-foreground">
        <Icon className="h-3.5 w-3.5 text-brand" strokeWidth={1.85} />
        {label}
      </div>
      <div className="mt-2 font-mono text-[1.625rem] font-medium leading-none tracking-[-0.04em] tabular-nums">
        {value ?? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
    </Link>
  );
}
